<script lang="ts" setup>
import dayjs from 'dayjs';
import { sleep } from '@antfu/utils';
import ToastComponent from '~/components/ui/ToastComponent.vue';
import { POSITION, useToast } from 'vue-toastification';
import { CATALOG_PAGE_TYPES } from '~/constants/catalogPageTypes';
import type { AdminPanelResponse } from '~/types/SiteSettings';

const { currentPageType, selectedOffer } = storeToRefs(useCatalogStore());
const siteStore = useSiteStore();
const route = useRoute();

const adminPanelData = ref<AdminPanelResponse | null>(null);

async function getPanelData() {
  await nextTick();
  await sleep(100);

  adminPanelData.value = await siteStore.getAdminPanelData();
}

const isAdminPanelOpen = ref(false);

const toggleAdminPanel = () => {
  isAdminPanelOpen.value = !isAdminPanelOpen.value;
  localStorage.setItem('is-admin-panel–open', JSON.stringify(isAdminPanelOpen.value));
};

onMounted(async () => {
  const savedAdminPanelState = localStorage.getItem('is-admin-panel–open');

  if (savedAdminPanelState) {
    isAdminPanelOpen.value = JSON.parse(savedAdminPanelState);
  }
  if (!adminPanelData.value) {
    await getPanelData();
  }
});

const isLinksDropdownOpen = ref(false);

const toggleLinksDropdown = () => {
  isLinksDropdownOpen.value = !isLinksDropdownOpen.value;
};

const { text, copy, copied, isSupported } = useClipboard();
const toast = useToast();

watch(
  () => copied.value,
  () => {
    if (copied.value)
      toast.info(
        { component: ToastComponent, props: { text: 'Скопировано: ' + text.value } },
        { timeout: 2000, position: POSITION.TOP_RIGHT },
      );
  },
);
</script>

<template>
  <div
    :class="{ 'admin-panel--open': isAdminPanelOpen }"
    class="admin-panel"
  >
    <div class="flex items-center flex-shrink-0 gap-2 *:p-1">
      <button @click="toggleAdminPanel">
        <SvgoNovaLogo
          :font-controlled="false"
          class="w-5 h-auto flex-shrink-0 text-label-blue hover:text-label-violet transition duration-200"
          filled
        />
      </button>

      <button
        :class="{ 'bg-black text-white': isLinksDropdownOpen }"
        class="relative h-6"
        @click="toggleLinksDropdown"
        @mouseenter="isLinksDropdownOpen = true"
        @mouseleave="isLinksDropdownOpen = false"
      >
        Ссылки
        <span
          v-show="isLinksDropdownOpen"
          class="absolute top-6 -left-2 flex flex-col text-start gap-3 w-40 p-2 bg-label-red text-black"
        >
          <a
            v-for="link in adminPanelData?.dropdown_links"
            :href="link.url"
            target="_blank"
          >
            {{ link.title }}
          </a>
        </span>
      </button>

      <a
        v-for="link in adminPanelData?.links"
        :href="link.url"
        target="_blank"
      >
        {{ link.title }}
      </a>

      <template
        v-if="
          [CATALOG_PAGE_TYPES.OFFER_PAGE, CATALOG_PAGE_TYPES.OFFER_GROUP_PAGE].includes(currentPageType) &&
          route.name === 'catalog-slug'
        "
      >
        <a
          :href="`/_admin/resources/product-nova-resources/${selectedOffer?.product_id}`"
          target="_blank"
        >
          Просмотр
        </a>

        <a
          :href="`/_admin/resources/product-nova-resources/${selectedOffer?.product_id}/edit`"
          target="_blank"
        >
          Редактирование
        </a>
      </template>
    </div>

    <div
      v-if="adminPanelData"
      class="admin-panel__dev-info *:h-full *:flex *:items-center *:text-nowrap *:gap-1"
    >
      <div
        class="bg-white px-2"
        style="color: #ff2d21"
      >
        laravel:
      </div>

      <div>
        <button
          v-if="isSupported"
          title="Копировать в буфер обмена"
          @click="copy(adminPanelData.branch)"
        >
          <SvgoCopy
            :font-controlled="false"
            class="copy-icon"
            filled
          />
        </button>
        {{ adminPanelData.branch }}
      </div>

      <div>
        <button
          v-if="isSupported"
          title="Копировать в буфер обмена"
          @click="copy(adminPanelData.commit)"
        >
          <SvgoCopy
            :font-controlled="false"
            class="copy-icon"
            filled
          />
        </button>
        {{ adminPanelData.commit }}
      </div>

      <div class="text-nowrap">{{ dayjs(adminPanelData.date).format('DD.MM.YYYY HH:mm') }}</div>

      <div
        class="text-black px-2"
        style="background-color: #06dc82"
      >
        nuxt:
      </div>

      <div>
        <button
          v-if="isSupported"
          title="Копировать в буфер обмена"
          @click="copy(adminPanelData.nuxt_branch)"
        >
          <SvgoCopy
            :font-controlled="false"
            class="copy-icon"
            filled
          />
        </button>
        {{ adminPanelData.nuxt_branch }}
      </div>

      <div>
        <button
          v-if="isSupported"
          title="Копировать в буфер обмена"
          @click="copy(adminPanelData.nuxt_commit)"
        >
          <SvgoCopy
            :font-controlled="false"
            class="copy-icon"
            filled
          />
        </button>
        {{ adminPanelData.nuxt_commit }}
      </div>

      <div class="text-nowrap">{{ dayjs(adminPanelData.nuxt_date).format('DD.MM.YYYY HH:mm') }}</div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.admin-panel {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 120;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  align-items: center;
  overflow: hidden;
  width: 28px;
  background-color: $label-red;
  font-size: $text-sm;

  &--open {
    overflow: visible;
    width: 100%;
    height: 32px;
  }

  &__dev-info {
    display: flex;
    flex-shrink: 1;
    gap: 8px;
    align-items: center;
    overflow-x: auto;
    overflow-y: hidden;
    height: 100%;
    margin-right: 8px;
    scrollbar-width: none;
  }
}

.copy-icon {
  width: 18px;
  height: 18px;
  color: #000000;
  transition: color 0.3s;

  &:hover {
    color: $black-80;
  }
}
</style>
